import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import ImmutablePropTypes from 'react-immutable-proptypes';
import { Map } from 'immutable';
import { Table, TableContainer, TableHead, TableRow, TableCell, TableBody, TextField, Paper } from '@mui/material';
import useMediaQuery from '@mui/material/useMediaQuery';
import variables from 'src/style/variable/variables.module.scss';
import { useTranslation } from 'src/i18n';
import { pairUp } from '../Commissioning/utils';
import DeviceTagAutocompleteField from '../Commissioning/DeviceTagAutocompleteField';

// Field lists define the order and set of fields rendered in each info section. They are
// hardcoded because the cable insulation form fields are fixed by product design.
const INFO_FIELDS = [
  'heatTraceCircuit', 'heatTraceCableTag',
  'unit', 'system',
  'heaterType', 'location',
  'lineNo', 'isoNumber'
];

export default function CableInsulationInfo (props) {
  const { i18nPrefix, data, onChange, readOnly, linkedDeviceTag, onUnlink, onDeviceSelect } = props;
  const { t } = useTranslation();
  const isMobile = useMediaQuery(`(max-width: ${variables.mobileWidth})`);

  // The pre/post forms are identical apart from the i18n namespace, which the parent supplies.
  // Per-field translation keys. Generic labels reuse existing keys (top-level / commissioningCircuit)
  // rather than duplicating them under this form's namespace; form-specific labels keep their own key.
  const infoLabelKeys: Record<string, string> = {
    heatTraceCircuit: `${i18nPrefix}.table.heatTraceCircuit`,
    heatTraceCableTag: `${i18nPrefix}.table.heatTraceCableTag`,
    unit: 'common.unit',
    system: 'system',
    heaterType: 'heaterType',
    location: 'location',
    lineNo: `${i18nPrefix}.table.lineNo`,
    isoNumber: 'isoNumber'
  };

  const [info, setInfo] = useState(Map(data?.get('info') || {}));

  useEffect(() => {
    setInfo(Map(data?.get('info') || {}));
  }, [data]);

  const onInfoChange = (key, val) => {
    setInfo(info.set(key, val));
    onChange(['extras', 'info', key], val);
  };

  const renderInfoField = (field) => {
    if (field === 'heatTraceCircuit') {
      return (
        <DeviceTagAutocompleteField
          value={(info.get(field) as string) || ''}
          disabled={readOnly}
          deviceType='circuit'
          placeholder={t(`${i18nPrefix}.table.heatTraceCircuit`) as string}
          linkedDeviceTag={linkedDeviceTag}
          onChange={(val) => onInfoChange(field, val)}
          onDeviceSelect={onDeviceSelect}
          onUnlink={onUnlink}
        />
      );
    }
    return (
      <TextField
        fullWidth
        disabled={readOnly}
        value={info.get(field) || ''}
        onChange={(e) => onInfoChange(field, e.target.value)}
      />
    );
  };

  const infoPairs = pairUp(INFO_FIELDS);

  const renderPairedTable = (pairs, labelFor, renderField) => (
    <TableContainer className={`default-control-panel-table${isMobile ? ' mobile-table' : ''}`}>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell className='sub-headers'>{t('common.field')}</TableCell>
            <TableCell className='sub-headers'>{t('common.value')}</TableCell>
            <TableCell className='sub-headers'>{t('common.field')}</TableCell>
            <TableCell className='sub-headers'>{t('common.value')}</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {pairs.map(([fieldA, fieldB]) => (
            <TableRow key={fieldA}>
              <TableCell className='sub-headers'>{t(labelFor(fieldA))}</TableCell>
              <TableCell className='body-cell'>{renderField(fieldA)}</TableCell>
              {fieldB ? (
                <>
                  <TableCell className='sub-headers'>{t(labelFor(fieldB))}</TableCell>
                  <TableCell className='body-cell'>{renderField(fieldB)}</TableCell>
                </>
              ) : (
                <>
                  <TableCell />
                  <TableCell />
                </>
              )}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );

  return (
    <div>
      <Paper sx={{ marginBottom: '30px' }}>
        <h3>{t(`${i18nPrefix}.modalTitle`)}</h3>
        {renderPairedTable(infoPairs, (field) => infoLabelKeys[field], renderInfoField)}
      </Paper>
    </div>
  );
}

CableInsulationInfo.propTypes = {
  i18nPrefix: PropTypes.string.isRequired,
  data: ImmutablePropTypes.map,
  onChange: PropTypes.func.isRequired,
  readOnly: PropTypes.bool,
  linkedDeviceTag: PropTypes.string,
  onUnlink: PropTypes.func,
  onDeviceSelect: PropTypes.func,
};

CableInsulationInfo.defaultProps = {
  readOnly: false,
  linkedDeviceTag: null,
  onUnlink: () => {},
  onDeviceSelect: null,
};
